import { useQuery } from "@tanstack/react-query";
import api from "api";
import { Card } from "common/Card";
import { IconButton, IconButtonSize } from "shared/components/IconButton";
import { Check, ChevronRight, Clock4, Divide } from "lucide-react";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger
} from "shared/components/Tooltip";
import { cx } from "shared/utils/classnames";
import { Fragment, useMemo } from "react";
import { useAuth } from "providers/auth";
import { Badge, BadgeSize, BadgeVariant } from "shared/components/Badge";
import { parseRvDate } from "helpers/parseRvDate";
import { RentalVerification } from "types/rental-verifications";

const RvGraph = ({ rvData }: { rvData: RentalVerification[] }) => {
  const { partner } = useAuth();

  const theme = useMemo(() => {
    const ret: Record<string, string> = {};
    if (partner?.colors?.primary && Object.keys(partner.colors.primary).length)
      Object.entries(partner.colors.primary).forEach(([key, value]) => {
        ret[`--primary-${key}`] = value;
      });

    if (partner?.base_border_radius)
      ret["--base-border-radius"] = partner.base_border_radius;

    if (partner?.colors?.background)
      Object.entries(partner.colors.background).forEach(([key, value]) => {
        ret[`--background-${key}`] = value;
      });

    if (partner?.font_family) {
      if (partner.font_family_link) {
        const link = document.createElement("link");
        link.rel = "stylesheet";
        link.href = partner?.font_family_link;
        if (partner.font_family_link.includes("fonts.googleapis.com")) {
          const preconnect = document.createElement("link");
          preconnect.rel = "preconnect";
          preconnect.href = "https://fonts.gstatic.com";
          document.head.appendChild(preconnect);
          const preconnect2 = document.createElement("link");
          preconnect2.rel = "preconnect";
          preconnect2.href = "https://fonts.googleapis.com";
          preconnect2.crossOrigin = "anonymous";
          document.head.appendChild(preconnect2);
        }
        document.head.appendChild(link);
      }

      ret["--font-family"] = partner.font_family;
    }
    return ret;
  }, [partner]);

  if (!rvData.length)
    return (
      <div className="w-full">
        <div className="flex items-center">
          <div className="h-1 bg-alpha-40 w-full" />
          <Tooltip open placement="bottom">
            <TooltipContent className="bg-white">
              <p className="text-[10px] px-2 py-1 rounded font-bold bg-alpha-40 text-content-200">
                Reported rent payments will be shown here
              </p>
            </TooltipContent>
            <TooltipTrigger>
              <div className="bg-white border-[3px] border-alpha-40 h-4 w-4 rounded-full shrink-0" />
            </TooltipTrigger>
          </Tooltip>
          <div className="h-1 bg-alpha-40 w-full" />
        </div>
      </div>
    );

  const max3Rv = rvData.slice(0, 3).reverse();

  return (
    <div className="w-full">
      <div className="flex items-center">
        <div
          className={cx(
            max3Rv.length === 1 ? "w-full" : "min-w-[50px]",
            "h-1 bg-primary-500"
          )}
        />
        {max3Rv.map((rv, idx) => (
          <Fragment key={rv.id}>
            <Tooltip open placement="bottom">
              <TooltipContent className="bg-white">
                <div style={theme}>
                  <p className="text-[10px] px-2 py-1 rounded font-bold bg-primary-50 text-primary-500">
                    {parseRvDate(rv.for_month)}
                  </p>
                </div>
              </TooltipContent>
              <TooltipTrigger>
                <div className="bg-white border-[3px] flex items-center justify-center border-primary-500 h-5 w-5 rounded-full shrink-0">
                  <Check
                    className="stroke-primary-500"
                    size={10}
                    strokeWidth={2}
                  />
                </div>
              </TooltipTrigger>
            </Tooltip>
            <div
              className={cx(
                "h-1",
                max3Rv.length === 1
                  ? "w-full bg-alpha-40"
                  : max3Rv.length === idx + 1
                    ? "bg-alpha-40 min-w-[50px]"
                    : "bg-primary-500 grow-1 w-full"
              )}
            />
          </Fragment>
        ))}
      </div>
    </div>
  );
};

export const RentReportingCard = ({
  enrollmentId,
  rentAmount,
  address,
  isPending,
  reason
}: {
  enrollmentId: number;
  rentAmount: number;
  address: string;
  isPending: boolean;
  reason: string;
}) => {
  const rv = useQuery({
    queryKey: ["REPORTING_HISTORY", enrollmentId],
    queryFn: () =>
      api
        .getRentalVerficationsOfEnrollmentCase({ id: enrollmentId })
        .then(({ data }) => data.items)
  });

  if (isPending)
    return (
      <Card
        className="max-w-[342px] w-full md:h-[316px] shrink-0 flex flex-col"
        linkTo={`/rent_reporting/${enrollmentId}`}
      >
        <div className="pb-4 flex flex-col md:h-[calc(100%-72px)]">
          <div className="px-4 h-[52px] flex items-center justify-between gap-2 border-b border-alpha-40">
            <h2 className="text-sm font-medium text-content-1000">
              Rent reporting
            </h2>

            <Badge
              variant={BadgeVariant.WARNING}
              size={BadgeSize.BASE}
              className="mr-auto ml-2"
            >
              Pending
            </Badge>

            <IconButton
              Icon={ChevronRight}
              size={IconButtonSize.SM}
              borderLess
              className="translate-x-2"
            />
          </div>

          <div className="border-b border-alpha-40 flex items-center justify-between gap-4 h-[76px] px-4">
            <div className="shrink-0">
              <p className="text-xs text-content-400 mb-2">Rent amount</p>
              <p className="text-sm text-content-1000 font-medium">
                ${(rentAmount / 100).toFixed(0)}
              </p>
            </div>

            <div className="flex-1">
              <p className="text-xs text-content-400 mb-2 text-right">
                Address
              </p>
              <p className="text-sm text-content-1000 font-medium truncate max-w-[220px] text-right !mr-0">
                {address}
              </p>
            </div>
          </div>
        </div>
        <div className="mt-auto h-12 bg-warning-50 border-t border-alpha-40 text-warning-700 font-bold flex text-xs items-center gap-2 justify-center rounded-b-lg">
          <Clock4 size={20} className="text-warning-700" />
          {reason}
        </div>
      </Card>
    );

  return (
    <Card
      className="max-w-[342px] w-full h-[316px] shrink-0"
      linkTo={`/rent_reporting/${enrollmentId}`}
    >
      <div className=" pb-4  flex flex-col h-[calc(100%-72px)]">
        <div className="px-4 h-[52px] flex items-center justify-between gap-2 border-b border-alpha-40">
          <h2 className="text-sm font-medium text-content-1000">
            Rent reporting
          </h2>

          <IconButton
            Icon={ChevronRight}
            size={IconButtonSize.SM}
            borderLess
            className="translate-x-2"
          />
        </div>

        <div className="border-b border-alpha-40 flex items-center justify-between gap-4 h-[76px] px-4">
          <div className="shrink-0">
            <p className="text-xs text-content-400 mb-2">Rent amount</p>
            <p className="text-sm text-content-1000 font-medium">
              ${(rentAmount / 100).toFixed(0)}
            </p>
          </div>

          <div className="flex-1">
            <p className="text-xs text-content-400 mb-2 text-right">Address</p>
            <p className="text-sm text-content-1000 font-medium truncate max-w-[220px] text-right !mr-0">
              {address}
            </p>
          </div>
        </div>
        <div className="">
          <p className="text-xs text-content-400 mb-2 ml-4 mt-4">
            Reporting history
          </p>

          {rv.data && (
            <div className="mt-12">
              <RvGraph rvData={rv.data} />
            </div>
          )}
        </div>
      </div>
    </Card>
  );
};
